// The marketplace listing source. v1 is LOCAL/CURATED and offers module-backed
// (in-process, first-party) plugins, which are signing-exempt (spec 4.1); real
// third-party sealed install pairs with Part 6 behind this SAME interface. A
// summary is the browse-card projection (no readme/manifest/install payload); a
// detail carries the full manifest, the UNTRUSTED readme, a bundled-asset
// resolver, and the installable payload.

import type { PluginManifest, PluginModule } from '@slate/plugin-api';
import type { InstalledPlugin } from '../plugins/runtime';
import { computeAccessBadge, type AccessBadge } from '../plugins/accessBadge';
import { EXAMPLE_MANIFEST, exampleModule } from '../plugins/builtinExample';
import {
  SLATE_CODER_MANIFEST,
  SLATE_CODER_README,
  slateCoderModule,
} from '../plugins/builtinSlateCoder';
import { GIT_MANIFEST, GIT_README, gitModule } from '../plugins/builtinGit';

export interface PluginSummary {
  id: string;
  name: string;
  version: string;
  publisher: string;
  description?: string;
  icon?: string;
  firstParty: boolean;
  categories?: string[];
  keywords?: string[];
  accessBadge: AccessBadge;
}

export interface PluginDetail extends PluginSummary {
  manifest: PluginManifest;
  readme: string;
  screenshots?: string[];
  resolveAsset(relPath: string): string | null;
  install: InstalledPlugin;
}

export interface PluginSource {
  listAvailable(): Promise<PluginSummary[]>;
  getDetail(id: string): Promise<PluginDetail>;
}

/** Project a detail down to its summary (drops readme/manifest/install). */
function toSummary(d: PluginDetail): PluginSummary {
  return {
    id: d.id,
    name: d.name,
    version: d.version,
    publisher: d.publisher,
    description: d.description,
    icon: d.icon,
    firstParty: d.firstParty,
    categories: d.categories,
    keywords: d.keywords,
    accessBadge: d.accessBadge,
  };
}

export function createLocalPluginSource(entries: PluginDetail[]): PluginSource {
  const byId = new Map(entries.map((e) => [e.id, e]));
  return {
    listAvailable: async () => [...byId.values()].map(toSummary),
    getDetail: async (id) => {
      const d = byId.get(id);
      if (!d) throw new Error(`unknown plugin "${id}"`);
      return d;
    },
  };
}

// Curated demo entries (module-backed, first-party, signing-exempt) ------------

/** Document statistics, all derived in a single linear scan of the text. */
export interface DocStats {
  /** Maximal runs of non-whitespace characters. */
  words: number;
  /** Total character count, whitespace included. */
  chars: number;
  /** Characters with every whitespace character removed. */
  charsNoSpaces: number;
  /** Newline-delimited lines (a non-empty document has at least one). */
  lines: number;
  /** Blocks of content separated by one or more blank lines. */
  paragraphs: number;
  /** Runs of terminal punctuation (a `.`, `!`, or `?` run counts once). */
  sentences: number;
}

/** Count words, characters, lines, paragraphs, and sentences in ONE pass over the
 *  text. Pure and allocation-free (it reads char codes, never building substrings),
 *  so it stays fast even on large documents. */
export function computeDocStats(text: string): DocStats {
  const chars = text.length;
  let charsNoSpaces = 0;
  let words = 0;
  let sentences = 0;
  let lines = chars > 0 ? 1 : 0;
  let paragraphs = 0;

  let inWord = false; // inside a run of non-whitespace
  let inParagraph = false; // inside a block of non-blank lines
  let lineHasContent = false; // the current line has seen a non-whitespace char
  let inTerminator = false; // inside a run of `.` / `!` / `?`

  for (let i = 0; i < chars; i++) {
    const code = text.charCodeAt(i);
    const isNewline = code === 10; // \n
    const isWhitespace =
      code === 32 /* space */ ||
      code === 9 /* \t */ ||
      code === 10 /* \n */ ||
      code === 13 /* \r */ ||
      code === 12 /* \f */ ||
      code === 11; /* \v */

    if (isNewline) {
      lines++;
      if (!lineHasContent) inParagraph = false; // a blank line ends the block
      lineHasContent = false;
    }

    if (isWhitespace) {
      inWord = false;
      inTerminator = false;
      continue;
    }

    charsNoSpaces++;
    lineHasContent = true;
    if (!inWord) {
      words++;
      inWord = true;
    }
    if (!inParagraph) {
      paragraphs++;
      inParagraph = true;
    }

    const isTerminator = code === 46 /* . */ || code === 33 /* ! */ || code === 63; /* ? */
    if (isTerminator && !inTerminator) sentences++;
    inTerminator = isTerminator;
  }

  return { words, chars, charsNoSpaces, lines, paragraphs, sentences };
}

/** Reading-time estimate at roughly 200 words per minute, rounded up. */
function readingTime(words: number): string {
  const minutes = words / 200;
  if (minutes < 1) return 'Less than 1 min read';
  return `${Math.ceil(minutes)} min read`;
}

/** Pluralize a count with its unit (regular plurals only). */
function plural(n: number, unit: string): string {
  return `${n.toLocaleString()} ${n === 1 ? unit : `${unit}s`}`;
}

/** A compact, multi-line summary of the document statistics for a message. */
export function formatDocStats(stats: DocStats): string {
  return [
    'Word Count',
    `${plural(stats.words, 'word')}, ${stats.chars.toLocaleString()} characters (${stats.charsNoSpaces.toLocaleString()} without spaces)`,
    `${plural(stats.lines, 'line')}, ${plural(stats.paragraphs, 'paragraph')}, ${plural(stats.sentences, 'sentence')}`,
    readingTime(stats.words),
  ].join('\n');
}

const wordcountModule: PluginModule = {
  activate(ctx) {
    ctx.subscriptions.push(
      ctx.slate.commands.register('wordcount.count', async () => {
        const text = await ctx.slate.editor.getActiveText();
        if (text == null) {
          await ctx.slate.window.showMessage('Word Count: open a document first.');
          return 0;
        }
        const stats = computeDocStats(text);
        await ctx.slate.window.showMessage(formatDocStats(stats));
        return stats.words;
      }),
    );
  },
};

const WORDCOUNT_MANIFEST: PluginManifest = {
  id: 'slate.wordcount',
  name: 'Word Count',
  version: '1.0.0',
  publisher: 'Slate',
  description:
    'Reports words, characters, lines, paragraphs, sentences, and reading time for your active document.',
  engines: { slate: '^1.0.0' },
  // Short slash prefix: this plugin's command invokes as `/wc:count` (or `/wc`).
  commandPrefix: 'wc',
  // Reads the active editor's text through the ungated editor API, so it needs no
  // mandatory permission; `net` stays optional (granted only if you fetch a guide).
  permissions: [
    { capability: 'net', reason: 'Fetch an optional style guide.', optional: true, domains: ['example.org'] },
  ],
  contributes: { commands: [{ id: 'wordcount.count', title: 'Word Count: Document statistics' }] },
};

const WORDCOUNT_README = [
  '# Word Count',
  '',
  'Reports detailed statistics for your active document. Run **Word Count:',
  'Document statistics** from the command palette, or type `/wc`.',
  '',
  'In a single fast pass it reports:',
  '',
  '- Words and characters (with and without spaces)',
  '- Lines, paragraphs, and sentences',
  '- Estimated reading time (about 200 words per minute)',
  '',
  'Works on any open file. It can also optionally fetch a style guide over the',
  'network, which you grant later.',
].join('\n');

const EXAMPLE_README = [
  '# Hello (Example)',
  '',
  'A tiny built-in example that shows the plugin model end to end: a command, an',
  'activity-bar view, a sandboxed panel surface, and a consented file read.',
  '',
  '- **Hello: Greet** posts a message from a sandboxed plugin.',
  '- **Hello: Report file size** asks for file-read permission, then reports a size.',
  '- **Hello: About** opens a modal surface.',
  '- **Hello: Ping the panel** sends a message to its panel view.',
  '',
  'Uninstall it any time from this page, then reinstall to run it again.',
].join('\n');

/** Bundled demo plugins the v1 marketplace can browse and install. The Hello
 *  example is curated here too: it ships installed at boot, so it lives under
 *  Installed, but listing it as available means uninstalling does not make it
 *  vanish, it simply returns to Available where it can be reinstalled. */
export const CURATED_PLUGINS: PluginDetail[] = [
  {
    id: SLATE_CODER_MANIFEST.id,
    name: SLATE_CODER_MANIFEST.name,
    version: SLATE_CODER_MANIFEST.version,
    publisher: SLATE_CODER_MANIFEST.publisher,
    description: SLATE_CODER_MANIFEST.description,
    firstParty: true,
    categories: ['Coding'],
    keywords: ['fold', 'folding', 'code', 'markdown', 'indent', 'outline'],
    accessBadge: computeAccessBadge(
      (SLATE_CODER_MANIFEST.permissions ?? []).map((p) => p.capability),
    ),
    manifest: SLATE_CODER_MANIFEST,
    readme: SLATE_CODER_README,
    resolveAsset: () => null,
    install: { manifest: SLATE_CODER_MANIFEST, module: slateCoderModule },
  },
  {
    id: GIT_MANIFEST.id,
    name: GIT_MANIFEST.name,
    version: GIT_MANIFEST.version,
    publisher: GIT_MANIFEST.publisher,
    description: GIT_MANIFEST.description,
    firstParty: true,
    categories: ['Source Control'],
    keywords: ['git', 'source control', 'commit', 'stage', 'version control', 'scm'],
    accessBadge: computeAccessBadge((GIT_MANIFEST.permissions ?? []).map((p) => p.capability)),
    manifest: GIT_MANIFEST,
    readme: GIT_README,
    resolveAsset: () => null,
    install: { manifest: GIT_MANIFEST, module: gitModule },
  },
  {
    id: 'slate.wordcount',
    name: 'Word Count',
    version: '1.0.0',
    publisher: 'Slate',
    description: WORDCOUNT_MANIFEST.description,
    firstParty: true,
    categories: ['Writing'],
    keywords: ['words', 'count', 'statistics', 'reading time'],
    accessBadge: computeAccessBadge((WORDCOUNT_MANIFEST.permissions ?? []).map((p) => p.capability)),
    manifest: WORDCOUNT_MANIFEST,
    readme: WORDCOUNT_README,
    resolveAsset: () => null,
    install: { manifest: WORDCOUNT_MANIFEST, module: wordcountModule },
  },
  {
    id: EXAMPLE_MANIFEST.id,
    name: EXAMPLE_MANIFEST.name,
    version: EXAMPLE_MANIFEST.version,
    publisher: EXAMPLE_MANIFEST.publisher,
    description: EXAMPLE_MANIFEST.description,
    firstParty: true,
    categories: ['Examples'],
    keywords: ['example', 'hello', 'demo', 'sample'],
    accessBadge: computeAccessBadge((EXAMPLE_MANIFEST.permissions ?? []).map((p) => p.capability)),
    manifest: EXAMPLE_MANIFEST,
    readme: EXAMPLE_README,
    resolveAsset: () => null,
    install: { manifest: EXAMPLE_MANIFEST, module: exampleModule },
  },
];
